import { Button } from '@finn/design-system/atoms/button';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalSubtitle,
  ModalTitle,
} from '@finn/design-system/atoms/modal';
import { Textarea } from '@finn/design-system/atoms/textarea';
import { ModalKey, useCloseModal, useIsModalOpen } from '@finn/ua-modals';
import { TrackingEventName, useTrackingStore } from '@finn/ua-tracking';
import { useIntl, useSession } from '@finn/ui-utils';
import { useCallback, useEffect, useState } from 'react';

interface FeedbackDialogConfig {
  modalKey: ModalKey;
  translationPrefix: string;
  trackingName: string;
  trackingLocation: string;
}

interface SubmissionHookReturn {
  submitPreference: (data: any) => Promise<boolean>;
  isLoading: boolean;
  isSuccess: boolean;
  error: string | null;
  reset: () => void;
}

interface FeedbackDialogProps {
  config: FeedbackDialogConfig;
  useSubmissionHook: () => SubmissionHookReturn;
  prepareSubmissionData: (comment: string, session: any) => any;
  validateInput?: (comment: string) => boolean;
  textareaRows?: number;
  onSuccessClose?: () => void;
}

export function FeedbackDialog({
  config,
  useSubmissionHook,
  prepareSubmissionData,
  validateInput = (comment) => !!comment.trim(),
  textareaRows = 3,
  onSuccessClose,
}: FeedbackDialogProps) {
  const intl = useIntl();
  const track = useTrackingStore((state) => state.track);
  const [session] = useSession();
  const isModalOpen = useIsModalOpen(config.modalKey);
  const closeModal = useCloseModal();

  const [comment, setComment] = useState('');
  const { submitPreference, isLoading, isSuccess, error, reset } =
    useSubmissionHook();

  const handleClose = useCallback(() => {
    closeModal(config.modalKey);
    reset();
    setComment('');
  }, [closeModal, reset, config.modalKey]);

  useEffect(() => {
    if (isModalOpen) {
      track(TrackingEventName.MODAL_OPENED, {
        name: config.trackingName,
        location: config.trackingLocation,
      });
    }
  }, [isModalOpen, track, config.trackingName, config.trackingLocation]);

  const handleSubmit = async () => {
    if (!validateInput(comment)) return;

    try {
      const submissionData = prepareSubmissionData(comment.trim(), session);

      if (!submissionData) {
        console.error('Failed to prepare submission data');

        return;
      }

      const success = await submitPreference(submissionData);

      if (success) {
        track(TrackingEventName.CTA_CLICKED, {
          location: config.trackingLocation,
        });
      }
    } catch (submitError) {
      console.error('Error submitting feedback:', submitError);
    }
  };

  if (isSuccess) {
    return (
      <Modal variant="small" open={isModalOpen} onOpenChange={handleClose}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {intl.formatMessage(`${config.translationPrefix}.successTitle`)}
            </ModalTitle>
            <ModalSubtitle>
              {intl.formatMessage(
                `${config.translationPrefix}.successSubtitle`
              )}
            </ModalSubtitle>
          </ModalHeader>
          <ModalFooter variant="small" className="mt-8">
            <Button
              onClick={() => {
                handleClose(); // Always close the modal
                onSuccessClose?.(); // Execute additional logic if provided
              }}
            >
              {intl.formatMessage(`${config.translationPrefix}.successCta`)}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal variant="small" open={isModalOpen} onOpenChange={handleClose}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            {intl.formatMessage(`${config.translationPrefix}.title`)}
          </ModalTitle>
          <ModalSubtitle>
            {intl.formatMessage(`${config.translationPrefix}.subtitle`)}
          </ModalSubtitle>
        </ModalHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <Textarea
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
            }}
            placeholder={intl.formatMessage(
              `${config.translationPrefix}.placeholder`
            )}
            rows={textareaRows}
            error={
              error
                ? intl.formatMessage(`${config.translationPrefix}.error`)
                : undefined
            }
          />
          <p className="mt-2 body-12-regular">
            {intl.formatMessage(`${config.translationPrefix}.hint`)}
          </p>
          <ModalFooter variant="small" className="mt-8">
            <Button
              type="submit"
              loading={isLoading}
              disabled={!validateInput(comment)}
            >
              {intl.formatMessage(`${config.translationPrefix}.submit`)}
            </Button>
            <Button onClick={handleClose} variant="ghost">
              {intl.formatMessage(`${config.translationPrefix}.cancel`)}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
